import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { SITE } from '../config/site'
import { Button } from './Button'
import styles from './SiteHeader.module.css'

interface NavItem {
  label: string
  href: string
}

/**
 * Marketing-site header. Deliberately separate from `components/Header.tsx`,
 * which belongs to the focused `/buy` checkout page and stays untouched.
 *
 * At rest the header is transparent and sits on the hero. Once the page moves
 * it materializes: hairline, blur, slightly denser padding. The state is
 * driven by an IntersectionObserver on a sentinel at the top of the document
 * rather than a scroll listener, which is both cheaper and more reliable.
 * Below 860px the mid-nav collapses to the wordmark plus the Buy action.
 */
const NAV: NavItem[] = [
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'Gestures', href: '/#gestures' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Support', href: `mailto:${SITE.supportEmail}` },
]

export function SiteHeader() {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const node = sentinelRef.current

    // Fallback for environments without IntersectionObserver.
    if (!node || typeof IntersectionObserver === 'undefined') {
      const onScroll = () => setScrolled(window.scrollY > 8)
      onScroll()
      window.addEventListener('scroll', onScroll, { passive: true })
      return () => window.removeEventListener('scroll', onScroll)
    }

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 1 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

      <header className={styles.header} data-scrolled={scrolled} role="banner">
        <div className={`container container--wide ${styles.inner}`}>
          <Link to="/" className={styles.wordmark} aria-label={`${SITE.productName}, home`}>
            {SITE.productName}
          </Link>

          <nav className={styles.nav} aria-label="Primary">
            <ul className={styles.navList}>
              {NAV.map((item) => (
                <li key={item.label}>
                  <a href={item.href} className={styles.navLink}>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.actions}>
            <Button to="/buy" variant="primary" size="sm">
              Buy {SITE.productName}
            </Button>
          </div>
        </div>
      </header>
    </>
  )
}
